import { yupResolver } from '@hookform/resolvers/yup'
import FormHelperText from '@mui/material/FormHelperText'
import { Typography } from '@mui/material'
import {
  ComplianceActivity,
  ComplianceLedger,
  PurchaseOrderItem,
} from 'types/graphql'
import * as yup from 'yup'

import { Form, useForm } from '@cedarjs/forms'
import { useMutation } from '@cedarjs/web'
import { toast } from 'react-hot-toast'

import { useDispatch } from 'src/hooks/useDispatch'
import { useSelector } from 'src/hooks/useSelector'
import { closeModal, openModal } from 'src/slices/modal'

import Button from 'src/components/MUI/Button'
import CreatableAutocomplete, {
  CreatableAutocompleteOption,
} from 'src/components/MUI/CreatableAutoComplete'
import { UsersDropDownSelector } from 'src/components/MUI/UsersDropDownSelector'
import useQuery from 'src/hooks/useQuery'
import Loading from 'src/components/Loading'
import { useOrgName } from 'src/hooks/useOrgName'
import useGetUsers from 'src/hooks/requests/useGetUsers'
import useUpdateComplianceLedger from 'src/hooks/requests/useUpdateComplianceLedger'
import { useEffect, useMemo } from 'react'
import getFormErrorArray from 'src/utils/getFormErrorArray'
import Drawer from './MUI/Drawer/Drawer'
import DrawerHeader from './MUI/Drawer/DrawerHeader'
import DrawerActions from './MUI/Drawer/DrawerActions'
import Checkbox from './MUI/Checkbox'
import { defaultLogbookPrintOptions } from '@wingwork/common/src/constants/printLogbookSettings'
import useCreateLogbookEntry from 'src/hooks/requests/useCreateLogbookEntry'
import Sentry from 'src/lib/sentry'
import { GET_COMPLIANCE_LEDGER_STATUS } from 'src/pages/BulkComplianceDetailPage/queries'
import { useApolloClient } from '@apollo/client'
import SplitButton from 'src/components/MUI/SplitButton'
import { GET_WORK_ORDER_BASE_FIELDS } from 'src/pages/WorkOrderCompliancePageV2/queries'
import { GET_COMPLIANCE_LEDGER_BASE_FIELDS } from 'src/pages/BulkCompliancePageV2/queries'

const GET_COMPLIANCE_LEDGER_BY_ID = gql`
  query getComplianceLedgerByIdToCreateLogbook($id: String!) {
    complianceLedger(id: $id) {
      id
      aircraftId
      complianceActivity(filters: { omitChildren: false }) {
        id
        maintenanceItem {
          id
          description
          ataCode
          adSbType
          manufactureCode
          title
          trackedByComponent {
            id
            name
          }
        }
        MaintenanceLogbookActivity {
          id
          hidden
          overrideItemCode
          overrideTitle
        }
      }
    }
  }
`

const RTS_STATEMENTS = gql`
  query GetRtsStatements($orgSlug: String!) {
    returnToServiceStatements(orgSlug: $orgSlug) {
      id
      statement
    }
  }
`

const CREATE_RTS_STATEMENT = gql`
  mutation CreateRtsStatement($input: CreateReturnToServiceStatementInput!) {
    createReturnToServiceStatement(input: $input) {
      id
    }
  }
`

type CreateLogbookEntryModalProps = {
  propLedgerId?: string
  onRequest?: ({ statementId, requestedWorkerId, requestedInspectorId }) => void
  /** Optionally define what happens when the user clicks backdrop, the close icon, or hits "escape" */
  onBail?: () => void
  onCompleted?: (item: PurchaseOrderItem) => void
  workOrderId?: string
}

const modalName = 'createLogbookEntryModal'

const CreateLogbookEntryModal: React.FC<CreateLogbookEntryModalProps> = ({
  propLedgerId,
  onRequest,
  onCompleted,
  onBail,
  workOrderId,
}) => {
  const dispatch = useDispatch()
  const orgSlug = useOrgName()
  const client = useApolloClient()

  const modalData = useSelector(
    (state) => state.modal.modals?.[modalName]?.data
  )

  // from work order flow, we pass in ledgerId from modalData instead of prop
  const ledgerId = propLedgerId ?? modalData?.ledgerId

  const { data: users } = useGetUsers()

  const {
    data: pageFetchData,
    loading,
    hasLoaded,
  } = useQuery(GET_COMPLIANCE_LEDGER_BY_ID, {
    variables: { id: ledgerId },
  })

  const complianceLedger: ComplianceLedger = pageFetchData?.complianceLedger

  const itemsGroupedByPrimaryComponentName = React.useMemo(() => {
    return (
      complianceLedger?.complianceActivity?.reduce((acc, value) => {
        const component = value?.maintenanceItem?.trackedByComponent
        if (component?.name === undefined) return acc
        if (acc[component.name]) {
          acc[component.name].push(value)
        } else {
          acc[component.name] = [value]
        }
        return acc
      }, {}) ?? {}
    )
  }, [complianceLedger])

  const getOverrideTitle = (item: ComplianceActivity) => {
    if (item?.MaintenanceLogbookActivity?.overrideTitle) {
      return item?.MaintenanceLogbookActivity?.overrideTitle
    }
    // Return the description for discrepancy items
    return item?.maintenanceItem?.title
  }

  const getWorkDetailsForForm = (items: ComplianceActivity[]) => {
    return items.map((item) => ({
      notes: '',
      complianceActivityId: item?.id,
      maintenanceLbActivityId: item?.MaintenanceLogbookActivity?.id ?? '',
      overrideItemCode: item?.MaintenanceLogbookActivity?.overrideItemCode,
      ataCode: item?.maintenanceItem?.ataCode,
      manufactureCode: item?.maintenanceItem?.manufactureCode,
      overrideTitle: getOverrideTitle(item),
      hidden: item?.MaintenanceLogbookActivity?.hidden ?? false,
    }))
  }

  const data = React.useMemo(() => {
    return Object.entries(itemsGroupedByPrimaryComponentName).map(
      ([componentName, items]) => ({
        activityItems: getWorkDetailsForForm(items),
        trackedByComponentId: items[0]?.maintenanceItem?.trackedByComponent?.id,
        title: `${componentName} Logbook Entry`,
        id: '', // this means that we are creating a new logbook entry
      })
    )
  }, [itemsGroupedByPrimaryComponentName])

  const { mutate: updateComplianceLedger, loading: updateLoading } =
    useUpdateComplianceLedger()

  const { bulkCreateLogbookEntry } = useCreateLogbookEntry()

  const [createRtsStatement] = useMutation(CREATE_RTS_STATEMENT)

  const RETURN_TO_SERVICE_STATEMENT_REQUIRED_ERROR =
    'Return to Service Statement is required'

  const schema = useMemo(
    () =>
      yup.object().shape({
        statementId: yup
          .object({
            value: yup
              .string()
              .test(
                'value-or-newvalue',
                RETURN_TO_SERVICE_STATEMENT_REQUIRED_ERROR,
                function (value) {
                  return Boolean(value || this.parent.newValue)
                }
              ),
          })
          .required(RETURN_TO_SERVICE_STATEMENT_REQUIRED_ERROR),
        requestedWorkerId: yup.string().nullable(),
        requestedInspectorId: yup
          .string()
          .nullable()
          .test(
            'different-technician-and-inspector',
            'Technician and Inspector cannot be the same user',
            function (value) {
              return (
                !value ||
                !this.parent.requestedWorkerId ||
                this.parent.requestedWorkerId !== value
              )
            }
          ),
        printSize: yup.string().required('Print size is required'),
        header: yup.string().required('Header is required'),
        sorting: yup.string().required('Sorting is required'),
        componentLayout: yup.string().required('Component layout is required'),
        includeDescriptionOfWork: yup.boolean(),
        technicianPlaceholderOnly: yup.boolean(),
        inspectorPlaceholderOnly: yup.boolean(),
        includeNotes: yup.boolean(),
        includeSubItems: yup.boolean(),
        includeATACodeOnly: yup.boolean(),
        includeTechnicianSignature: yup
          .boolean()
          .test(
            'technician-selection-or-placeholder',
            'When including a Technician signature, either select a Technician or enable Placeholder Only.',
            function (value) {
              const { technicianPlaceholderOnly, requestedWorkerId } =
                this.parent
              if (!value) return true
              return Boolean(technicianPlaceholderOnly || requestedWorkerId)
            }
          ),
        includeInspectorSignature: yup
          .boolean()
          .test(
            'inspector-selection-or-placeholder',
            'When including an Inspector signature, either select an Inspector or enable Placeholder Only.',
            function (value) {
              const { inspectorPlaceholderOnly, requestedInspectorId } =
                this.parent
              if (!value) return true
              return Boolean(inspectorPlaceholderOnly || requestedInspectorId)
            }
          ),
        includeCorrectiveActionNotes: yup.boolean(),
        includePartRemovalReasonAndInstalledPartStatus: yup.boolean(),
        includeNextDueInformation: yup.boolean(),
        includePDFAttachments: yup.boolean(),
      }),
    [itemsGroupedByPrimaryComponentName]
  )

  const formMethods = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultLogbookPrintOptions,
      submitButton: '',
    },
  })

  const formRequestedWorkerId = formMethods.watch('requestedWorkerId')
  const formRequestedInspectorId = formMethods.watch('requestedInspectorId')
  const formTechnicianPlaceholderOnly = formMethods.watch(
    'technicianPlaceholderOnly'
  )
  const formInspectorPlaceholderOnly = formMethods.watch(
    'inspectorPlaceholderOnly'
  )
  const formIncludeTechnicianSignature = formMethods.watch(
    'includeTechnicianSignature'
  )
  const formIncludeInspectorSignature = formMethods.watch(
    'includeInspectorSignature'
  )

  // Keep field-level error on includeTechnicianSignature in sync with dependencies
  useEffect(() => {
    if (!formIncludeTechnicianSignature) {
      formMethods.clearErrors('includeTechnicianSignature')
      return
    }
    const isValid = Boolean(
      formTechnicianPlaceholderOnly || formRequestedWorkerId
    )
    if (isValid) {
      formMethods.clearErrors('includeTechnicianSignature')
    } else {
      formMethods.setError('includeTechnicianSignature', {
        message:
          'When including a Technician signature, either select a Technician or enable Placeholder Only.',
      })
    }
  }, [
    formIncludeTechnicianSignature,
    formTechnicianPlaceholderOnly,
    formRequestedWorkerId,
  ])

  // Keep field-level error on includeInspectorSignature in sync with dependencies
  useEffect(() => {
    if (!formIncludeInspectorSignature) {
      formMethods.clearErrors('includeInspectorSignature')
      return
    }
    const isValid = Boolean(
      formInspectorPlaceholderOnly || formRequestedInspectorId
    )
    if (isValid) {
      formMethods.clearErrors('includeInspectorSignature')
    } else {
      formMethods.setError('includeInspectorSignature', {
        message:
          'When including an Inspector signature, either select an Inspector or enable Placeholder Only.',
      })
    }
  }, [
    formIncludeInspectorSignature,
    formInspectorPlaceholderOnly,
    formRequestedInspectorId,
  ])

  useEffect(() => {
    if (formTechnicianPlaceholderOnly) {
      formMethods.setValue('requestedWorkerId', undefined)
    }
  }, [formTechnicianPlaceholderOnly])

  // Re-validate the includeTechnicianSignature field when its dependencies change
  useEffect(() => {
    formMethods.trigger('includeTechnicianSignature')
  }, [formRequestedWorkerId, formTechnicianPlaceholderOnly])

  useEffect(() => {
    if (formInspectorPlaceholderOnly) {
      formMethods.setValue('requestedInspectorId', undefined)
    }
  }, [formInspectorPlaceholderOnly])

  useEffect(() => {
    if (!formIncludeTechnicianSignature) {
      formMethods.setValue('technicianPlaceholderOnly', false)
      formMethods.setValue('requestedWorkerId', undefined)
    }
  }, [formIncludeTechnicianSignature])

  useEffect(() => {
    if (!formIncludeInspectorSignature) {
      formMethods.setValue('inspectorPlaceholderOnly', false)
      formMethods.setValue('requestedInspectorId', undefined)
    }
  }, [formIncludeInspectorSignature])

  // Set an error on the Inspector field if the Technician and Inspector fields are the same
  useEffect(() => {
    if (
      formRequestedWorkerId &&
      formRequestedInspectorId &&
      formRequestedWorkerId === formRequestedInspectorId
    ) {
      formMethods.setError('requestedInspectorId', {
        message: 'Technician and Inspector cannot be the same user',
      })
    } else {
      formMethods.clearErrors('requestedInspectorId')
    }
  }, [formRequestedWorkerId, formRequestedInspectorId])

  const hasFormErrors = !!Object.keys(formMethods.formState.errors).length
  const formErrors = getFormErrorArray(formMethods.formState.errors)

  const open =
    useSelector((state) => state.modal.modals?.[modalName]?.isOpen) ?? false

  const handleClose = () => {
    dispatch(closeModal({ name: modalName }))
  }

  const handleBail = () => {
    handleClose()
    onBail?.()
  }

  useEffect(() => {
    if (!open) {
      formMethods.reset({
        ...defaultLogbookPrintOptions,
        submitButton: '',
      })
    }
  }, [open])

  const handleSubmit = async (values) => {
    console.log('values', values)
    const {
      requestedWorkerId,
      requestedInspectorId,
      statementId: formStatementId,
      submitButton,
      technicianPlaceholderOnly: _,
      inspectorPlaceholderOnly: __,
      ...logbookPdfOptions
    } = values

    let statementId = formStatementId ? formStatementId.value : null
    if (formStatementId && formStatementId.isNew) {
      const { data: rtsStatementData } = await createRtsStatement({
        variables: {
          input: {
            orgSlug,
            statement: formStatementId.newValue,
          },
        },
      })
      statementId = rtsStatementData.createReturnToServiceStatement.id
    }
    const doSign = submitButton === 'signAndPublish'

    // publish the logbook entries

    await bulkCreateLogbookEntry(
      {
        updateComplianceLedger: {
          requestedWorkerId,
          requestedInspectorId,
          status: doSign ? 'SIGN' : 'COMPLETED_LOGBOOK',
          statementId,
        },
        aircraftId: complianceLedger?.aircraftId,
        complianceLedgerId: ledgerId,
        doSign,
        status: 'PUBLISHED',
        pdfOptions: logbookPdfOptions,
        logbooks: data,
      },
      (cache) => {
        const existingData = cache.readQuery({
          query: GET_COMPLIANCE_LEDGER_STATUS,
          variables: { id: ledgerId },
        })

        const updatedData = {
          ...existingData,
          complianceLedger: {
            ...existingData.complianceLedger,
            status: doSign ? 'SIGN' : 'COMPLETED_LOGBOOK',
          },
        }
        cache.writeQuery({
          query: GET_COMPLIANCE_LEDGER_STATUS,
          variables: { id: ledgerId },
          data: updatedData,
        })

        // update the requestedInspectorId and/or requestedWorkerId so the UI that is conditionally rendered based on isUserRequestedAsNextSigner is updated
        if (workOrderId) {
          const workOrderOldData = cache.readQuery({
            query: GET_WORK_ORDER_BASE_FIELDS,
            variables: { id: workOrderId },
          })
          cache.writeQuery({
            query: GET_WORK_ORDER_BASE_FIELDS,
            variables: { id: workOrderId },
            data: {
              ...workOrderOldData,
              internalWorkOrder: {
                ...workOrderOldData.internalWorkOrder,
                ...(requestedInspectorId && { requestedInspectorId }),
                ...(requestedWorkerId && { requestedWorkerId }),
              },
            },
          })
        } else {
          const ledgerOldData = cache.readQuery({
            query: GET_COMPLIANCE_LEDGER_BASE_FIELDS,
            variables: { id: ledgerId },
          })
          cache.writeQuery({
            query: GET_COMPLIANCE_LEDGER_BASE_FIELDS,
            variables: { id: ledgerId },
            data: {
              ...ledgerOldData,
              complianceLedger: {
                ...ledgerOldData.complianceLedger,
                ...(requestedInspectorId && { requestedInspectorId }),
                ...(requestedWorkerId && { requestedWorkerId }),
              },
            },
          })
        }
      }
    )

    dispatch(
      openModal({
        name: 'viewLogbookEntryModal',
      })
    )

    handleClose()
    onRequest?.(values)
  }

  const handleError = (toastId: string) => (e: Error) => {
    Sentry.captureException(e)
    toast.error('Error generating pdf.\nplease contact support.', {
      id: toastId,
    })
  }

  const {
    data: rtsData,
    hasLoaded: rtsStatementsLoaded,
    loading: rtsStatementsLoading,
  } = useQuery(RTS_STATEMENTS, {
    variables: { orgSlug: orgSlug },
  })

  const statementOptions: CreatableAutocompleteOption[] =
    rtsData?.returnToServiceStatements.map((statement) => ({
      label: statement.statement,
      value: statement.id,
    }))

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        handleBail()
      }}
      title="Publish Logbook Entry"
    >
      <DrawerHeader title="Publish Logbook Entry" onClose={handleBail} />
      {loading ? (
        <div className="flex h-full w-[800px] items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="h-full w-[800px] overflow-y-auto p-3">
          <Form
            id="createLogbookEntryForm"
            className="h-full"
            formMethods={formMethods}
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <Typography variant="caption" className="text-blueGrey-600">
                  BULK RETURN TO SERVICE STATEMENT
                </Typography>
                <CreatableAutocomplete
                  isFormField
                  name="statementId"
                  options={statementOptions}
                  growHeight
                />
              </div>
              <div className="flex flex-col gap-2">
                <Typography variant="caption" className="text-blueGrey-600">
                  SIGNATURES
                </Typography>
                <div className="flex gap-2">
                  <div className="flex w-1/2 basis-1/2 flex-col gap-1">
                    <Checkbox
                      name="includeTechnicianSignature"
                      label="Include Technician Signature"
                    />
                    <Checkbox
                      name="technicianPlaceholderOnly"
                      label="Placeholder Only (for wet signatures)"
                      disabled={!formIncludeTechnicianSignature}
                    />
                    <UsersDropDownSelector
                      name="requestedWorkerId"
                      label="Technician"
                      users={users?.users || []}
                      className="flex-1"
                      disabled={
                        formTechnicianPlaceholderOnly ||
                        !formIncludeTechnicianSignature
                      }
                    />
                    <FormHelperText
                      error={
                        formIncludeTechnicianSignature &&
                        !formTechnicianPlaceholderOnly &&
                        !formRequestedWorkerId
                      }
                    >
                      {formIncludeTechnicianSignature &&
                      !formTechnicianPlaceholderOnly &&
                      !formRequestedWorkerId
                        ? 'Please select a Technician or enable Placeholder Only.'
                        : ''}
                    </FormHelperText>
                  </div>
                  <div className="flex w-1/2 basis-1/2 flex-col gap-1">
                    <Checkbox
                      name="includeInspectorSignature"
                      label="Include Inspector Signature"
                    />
                    <Checkbox
                      name="inspectorPlaceholderOnly"
                      label="Placeholder Only (for wet signatures)"
                      disabled={!formIncludeInspectorSignature}
                    />
                    <UsersDropDownSelector
                      name="requestedInspectorId"
                      label="Inspector"
                      users={users?.users || []}
                      className="flex-1"
                      disabled={
                        formInspectorPlaceholderOnly ||
                        !formIncludeInspectorSignature
                      }
                    />
                    <FormHelperText
                      error={
                        formIncludeInspectorSignature &&
                        !formInspectorPlaceholderOnly &&
                        !formRequestedInspectorId
                      }
                    >
                      {formIncludeInspectorSignature &&
                      !formInspectorPlaceholderOnly &&
                      !formRequestedInspectorId
                        ? 'Please select an Inspector or enable Placeholder Only.'
                        : ''}
                    </FormHelperText>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Typography variant="caption" className="text-blueGrey-600">
                  PRINT OPTIONS
                </Typography>
                <div className="flex flex-col gap-2">
                  <Checkbox
                    name="includeDescriptionOfWork"
                    label="Include Description of Work"
                  />
                  <Checkbox name="includeNotes" label="Include Notes" />
                  {/* <Checkbox
                    name="includeATACodeOnly"
                    label="Include ATA Code Only"
                  /> */}
                  <Checkbox
                    name="includePartRemovalReasonAndInstalledPartStatus"
                    label="Include Part Removal Reason and Installed Part Status"
                  />
                  <Checkbox
                    name="includeNextDueInformation"
                    label="Include Next Due Information"
                  />
                  <Checkbox
                    name="includeChildTasks"
                    label="Include Child Tasks"
                  />
                  <Checkbox
                    name="includeDiscrepancies"
                    label="Include Discrepancies"
                  />
                  <Checkbox
                    name="includeCustomTasks"
                    label="Include Custom Tasks"
                  />
                  {/* <Checkbox
                name="includeSubItems"
                checkboxProps={{ disabled: true }}
                label="Include Sub Items"
              />
              <Checkbox
                name="includePDFAttachments"
                checkboxProps={{ disabled: true }}
                label="Include PDF Attachments"
              /> */}
                  <Checkbox
                    name="includeCorrectiveActionNotes"
                    label="Include Corrective Action Notes"
                  />
                </div>
              </div>
            </div>
          </Form>
        </div>
      )}
      <DrawerActions
        className="gap-1"
        leftActions={
          <Button variant="outlined" color="base" onClick={handleBail}>
            CANCEL
          </Button>
        }
        rightActions={
          <SplitButton
            size="medium"
            disabledTooltip={formErrors[0]}
            initialSelectedIndex={0}
            options={[
              {
                label: 'Publish',
                disabled: hasFormErrors || loading || updateLoading,
                onClick: () => {
                  formMethods.setValue('submitButton', 'publish')
                  formMethods.handleSubmit(handleSubmit)()
                },
              },
              {
                label: 'e-Sign and Publish',
                disabled:
                  hasFormErrors ||
                  loading ||
                  updateLoading ||
                  !(formRequestedWorkerId || formRequestedInspectorId),
                onClick: () => {
                  formMethods.setValue('submitButton', 'signAndPublish')
                  formMethods.handleSubmit(handleSubmit)()
                },
              },
            ]}
          />
        }
      />
    </Drawer>
  )
}

export default CreateLogbookEntryModal
